import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, MessageSquare, Plus, X, Trash2, Users, ArrowLeft } from "lucide-react";
import { API, ROLE_LABELS, ROLE_BADGE, formatError } from "./constants";

export const ChatView = ({ user }) => {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState("all");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [picked, setPicked] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [newError, setNewError] = useState("");
  const [creating, setCreating] = useState(false);
  const [mobilePane, setMobilePane] = useState(false);
  const boxRef = useRef(null);

  const fetchConvos = () => {
    axios.get(`${API}/chat/conversations`, { withCredentials: true })
      .then((res) => setConvos(res.data))
      .catch(() => {});
  };

  const fetchMessages = (cid) => {
    axios.get(`${API}/chat/conversations/${cid}/messages`, { withCredentials: true })
      .then((res) => setMessages(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchConvos();
    const t = setInterval(fetchConvos, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!active) return;
    fetchMessages(active);
    axios.post(`${API}/chat/read`, { conversation_id: active }, { withCredentials: true }).catch(() => {});
    const t = setInterval(() => fetchMessages(active), 4000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const openConvo = (cid) => {
    setActive(cid);
    setMobilePane(true);
    axios.post(`${API}/chat/read`, { conversation_id: cid }, { withCredentials: true }).catch(() => {});
    setConvos((prev) => prev.map((c) => (c.id === cid ? { ...c, unread: 0 } : c)));
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/conversations/${active}/messages`, { text }, { withCredentials: true });
      setText("");
      fetchMessages(active);
    } catch {
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async (mid) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await axios.delete(`${API}/chat/messages/${mid}`, { withCredentials: true });
      fetchMessages(active);
    } catch {
    }
  };

  const deleteConvo = async (cid) => {
    if (!window.confirm("Delete this conversation and all its messages for everyone?")) return;
    try {
      await axios.delete(`${API}/chat/conversations/${cid}`, { withCredentials: true });
      setActive("all");
      setMobilePane(false);
      fetchConvos();
    } catch {
    }
  };

  const openNew = () => {
    setShowNew(true);
    setPicked([]);
    setGroupName("");
    setNewError("");
    axios.get(`${API}/users/directory`, { withCredentials: true })
      .then((res) => setDirectory(res.data.filter((u) => u.id !== user.id)))
      .catch(() => setDirectory([]));
  };

  const createConvo = async () => {
    if (picked.length === 0) return;
    setCreating(true);
    setNewError("");
    try {
      const payload = picked.length === 1
        ? { type: "direct", member_ids: picked }
        : { type: "group", member_ids: picked, name: groupName.trim() };
      const res = await axios.post(`${API}/chat/conversations`, payload, { withCredentials: true });
      setShowNew(false);
      fetchConvos();
      openConvo(res.data.id);
    } catch (err) {
      setNewError(formatError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const activeConvo = convos.find((c) => c.id === active);
  const canDeleteConvo = activeConvo && activeConvo.type !== "all" &&
    (activeConvo.type === "direct" || activeConvo.created_by === user.id || user.role === "cofounder");

  return (
    <div className="mt-6 flex h-[calc(100vh-260px)] min-h-[420px] overflow-hidden rounded-xl border border-border bg-white shadow-soft" data-testid="chat-view">
      {/* Conversation list */}
      <div className={`w-full flex-col border-r border-border md:flex md:w-72 ${mobilePane ? "hidden" : "flex"}`} data-testid="chat-convo-list">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-amberdark" />
            <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">Team Chat</h2>
          </div>
          <button
            onClick={openNew}
            data-testid="chat-new-btn"
            aria-label="New chat or group"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-500 transition-colors duration-200 hover:border-amber hover:bg-amber/10 hover:text-amberdark"
          >
            <Plus size={15} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-lenis-prevent="true">
          {convos.map((c) => (
            <button
              key={c.id}
              onClick={() => openConvo(c.id)}
              data-testid={`chat-convo-${c.id}`}
              className={`flex w-full items-start gap-3 border-b border-border/60 px-5 py-3.5 text-left transition-colors duration-200 hover:bg-cream ${active === c.id ? "bg-cream" : ""}`}
            >
              <span className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold ${c.type === "all" ? "bg-navy text-amber" : c.type === "group" ? "bg-amber/20 text-amberdark" : "bg-sand text-navy"}`}>
                {c.type === "all" ? <Users size={14} /> : c.name.slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-bold text-navy">{c.name}</span>
                  {c.unread > 0 && (
                    <span className="shrink-0 rounded-full bg-amber px-1.5 py-0.5 text-[9px] font-extrabold text-navy" data-testid={`chat-convo-unread-${c.id}`}>
                      {c.unread}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-xs text-gray-500">
                  {c.last_message ? `${c.last_message.user_name}: ${c.last_message.text}` : "No messages yet"}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Message pane */}
      <div className={`flex-1 flex-col md:flex ${mobilePane ? "flex" : "hidden"}`} data-testid="chat-pane">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobilePane(false)} data-testid="chat-back-btn" aria-label="Back to conversations" className="text-gray-500 md:hidden">
              <ArrowLeft size={18} />
            </button>
            <div>
              <p className="font-display text-base font-extrabold tracking-tight text-navy" data-testid="chat-pane-title">
                {activeConvo?.name || "Everyone"}
              </p>
              <p className="text-[11px] text-gray-500">
                {activeConvo?.type === "all" && "Everyone on the team sees this"}
                {activeConvo?.type === "direct" && "Private — just the two of you"}
                {activeConvo?.type === "group" && `${activeConvo.members.length} members`}
              </p>
            </div>
          </div>
          {canDeleteConvo && (
            <button
              onClick={() => deleteConvo(active)}
              data-testid="chat-delete-convo-btn"
              aria-label="Delete conversation"
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-400 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>

        <div ref={boxRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="chat-messages" data-lenis-prevent="true">
          {messages.length === 0 ? (
            <p className="py-10 text-center text-xs font-bold uppercase tracking-[0.2em] text-gray-400" data-testid="chat-empty">
              No messages yet. Say hello.
            </p>
          ) : (
            messages.map((m) => {
              const mine = m.user_id === user.id;
              const canDelete = mine || user.role === "cofounder";
              return (
                <div key={m.id} className={`group flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-row-${m.id}`}>
                  <div className={`max-w-[75%] rounded-xl px-4 py-3 ${mine ? "bg-navy text-white" : "bg-cream text-navy"}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold">{m.user_name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${ROLE_BADGE[m.role] || ROLE_BADGE.staff}`}>
                        {ROLE_LABELS[m.role] || "Staff"}
                      </span>
                      <span className={`text-[10px] ${mine ? "text-gray-300" : "text-gray-400"}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p>
                  </div>
                  {canDelete && (
                    <button
                      onClick={() => deleteMessage(m.id)}
                      data-testid={`chat-message-delete-${m.id}`}
                      aria-label="Delete message"
                      className="mb-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-300 opacity-0 transition-opacity duration-200 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <form onSubmit={send} className="flex items-center gap-3 border-t border-border px-5 py-4" data-testid="chat-form">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message..."
            data-testid="chat-input"
            className="flex-1 rounded-md border border-border bg-white px-4 py-2.5 text-sm text-navy outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-amber"
          />
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={sending || !text.trim()}
            className="flex items-center gap-2 rounded-md bg-amber px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50"
          >
            <Send size={14} />
            Send
          </button>
        </form>
      </div>

      {/* New chat / group modal */}
      {showNew && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm" data-testid="chat-new-modal" onClick={() => setShowNew(false)}>
          <div className="relative max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-8 shadow-lift" data-lenis-prevent="true" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowNew(false)} aria-label="Close" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-border text-gray-500 transition-colors duration-300 hover:border-navy hover:text-navy">
              <X size={16} />
            </button>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy">New chat</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Pick one person for a private chat, or several to create a group.
            </p>
            <div className="mt-6 space-y-2" data-testid="chat-member-list">
              {directory.length === 0 && (
                <p className="text-sm text-gray-500">No other team members yet — add them from the Team tab.</p>
              )}
              {directory.map((m) => {
                const on = picked.includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => setPicked((prev) => (on ? prev.filter((p) => p !== m.id) : [...prev, m.id]))}
                    data-testid={`chat-member-${m.id}`}
                    className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left transition-colors duration-200 ${on ? "border-amber bg-amber/10" : "border-border hover:border-amber/50"}`}
                  >
                    <span>
                      <span className="block text-sm font-bold text-navy">{m.name}</span>
                      <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${ROLE_BADGE[m.role] || ROLE_BADGE.staff}`}>
                        {ROLE_LABELS[m.role] || "Staff"}
                      </span>
                    </span>
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold ${on ? "border-amber bg-amber text-navy" : "border-border text-transparent"}`}>✓</span>
                  </button>
                );
              })}
            </div>
            {picked.length > 1 && (
              <input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Group name, e.g. Site Team Bangalore"
                data-testid="chat-group-name-input"
                className="field-input mt-4"
              />
            )}
            {newError && <p className="mt-3 text-sm font-semibold text-red-600" data-testid="chat-new-error">{newError}</p>}
            <button
              onClick={createConvo}
              data-testid="chat-create-btn"
              disabled={creating || picked.length === 0 || (picked.length > 1 && !groupName.trim())}
              className="mt-5 w-full rounded-md bg-amber px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50"
            >
              {creating ? "Creating..." : picked.length > 1 ? "Create Group" : "Start Chat"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
